import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import Icon from '../../../../Assets/Icon/CustomIcon';
// [버튼] 하단 버튼 공통 컴포넌트
import { BottomBar, PrimaryButton } from '../ui/BottomActionBar';

const MAX_EXTEND_HOURS = 4;

const EXTEND_HOUR_OPTIONS = [1, 2, 3, 4];

function addHoursToTime(hhmm, hours) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + hours * 60;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

function WorkTypeCard({ label, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex w-full items-center justify-between rounded-2xl px-5 py-3 text-left transition-all duration-200 active:scale-[0.98] ${
        selected
          ? 'border-primary border-2 bg-white shadow-[0_10px_30px_rgba(0,110,11,0.08)]'
          : 'border border-slate-200/60 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)]'
      } `}
    >
      <span className="font-headline text-on-surface text-base font-semibold">
        {label}
      </span>

      <div
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-200 ${selected ? 'bg-primary border-primary' : 'border-2 border-slate-300 bg-transparent'} `}
      >
        {selected && <Icon.Check className="h-3.5 w-3.5 text-white" />}
      </div>
    </button>
  );
}

function StatusInfoCard({ currentEndTime, extendHours, onExtendHoursChange }) {
  const expectedEndTime = useMemo(
    () => addHoursToTime(currentEndTime, extendHours),
    [currentEndTime, extendHours]
  );

  return (
    <section className="overflow-hidden rounded-3xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.07)]">
      <div className="bg-primary/8 flex items-center justify-between border-b border-[#5ABD4F]/10 px-5 py-4">
        <div className="flex items-center gap-2">
          <div className="bg-primary/15 flex h-8 w-8 items-center justify-center rounded-full">
            <Icon.Verified className="text-primary h-4 w-4" />
          </div>
          <span className="font-headline text-on-surface text-sm font-extrabold tracking-wide">
            현황 정보
          </span>
        </div>
        <span className="rounded-full bg-[#5ABD4F]/10 px-3 py-1 text-xs font-bold text-[#5ABD4F]">
          최대 {MAX_EXTEND_HOURS}시간
        </span>
      </div>

      <div className="space-y-5 p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-1 flex-col items-center gap-1 rounded-2xl bg-slate-50 px-4 py-3">
            <span className="text-2xs font-bold tracking-widest text-slate-400 uppercase">
              현재 종료
            </span>
            <div className="flex items-baseline gap-0.5">
              <span className="font-headline text-2xl font-extrabold text-slate-700">
                {currentEndTime.split(':')[0]}
              </span>
              <span className="font-headline text-lg font-bold text-slate-400">
                :
              </span>
              <span className="font-headline text-2xl font-extrabold text-slate-700">
                {currentEndTime.split(':')[1]}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-0.5">
            <div className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-full">
              <Icon.ArrowForward className="text-primary h-4 w-4" />
            </div>
            <span className="font-headline text-primary text-2xs font-extrabold">
              +{extendHours}h
            </span>
          </div>

          <div className="bg-primary/8 flex flex-1 flex-col items-center gap-1 rounded-2xl border-2 border-[#5ABD4F]/25 px-4 py-3">
            <span className="text-2xs font-bold tracking-widest text-[#5ABD4F] uppercase">
              연장 후 종료
            </span>
            <div className="flex items-baseline gap-0.5">
              <span className="font-headline text-primary text-2xl font-extrabold">
                {expectedEndTime.split(':')[0]}
              </span>
              <span className="font-headline text-primary/60 text-lg font-bold">
                :
              </span>
              <span className="font-headline text-primary text-2xl font-extrabold">
                {expectedEndTime.split(':')[1]}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-on-surface-variant text-xs font-bold tracking-wider uppercase">
            연장 시간 선택
          </span>
          <div className="grid grid-cols-4 gap-2">
            {EXTEND_HOUR_OPTIONS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => onExtendHoursChange(h)}
                className={`font-headline rounded-full py-2.5 text-sm font-extrabold transition-all duration-150 active:scale-95 ${
                  extendHours === h
                    ? 'bg-primary text-white shadow-md shadow-[#5ABD4F]/30'
                    : 'border border-slate-200 bg-white text-slate-500'
                }`}
              >
                +{h}h
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ExtendWorkPage({ onSubmit, permitData }) {
  const dispatch = useDispatch();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [extendHours, setExtendHours] = useState(MAX_EXTEND_HOURS);

  const currentEndTime = permitData?.currentEndTime ?? '00:00';
  const workTypes = permitData?.workTypes ?? [];

  const handleToggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    if (selectedIds.size === 0) {
      dispatch(
        showSnackbar({
          message: '연장할 작업을 하나 이상 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const selectedDetails = workTypes.filter((w) => selectedIds.has(w.id));
    const baseEndTime = selectedDetails.reduce(
      (latest, w) => (w.endTime > latest ? w.endTime : latest),
      selectedDetails[0]?.endTime ?? currentEndTime
    );
    const extEndTime = addHoursToTime(baseEndTime, extendHours);

    onSubmit?.({
      dtlCds: [...selectedIds],
      extEndTime,
    });
  };

  return (
    <div className="font-body bg-surface text-on-surface min-h-screen pb-64">
      <main className="mx-auto mb-70 max-w-md px-6">
        <section className="mb-2">
          <h2 className="font-headline text-on-surface text-lg leading-snug font-bold tracking-tight">
            연장할 작업을 선택해주세요.
          </h2>
          <p className="text-sm text-slate-400">
            진행 중인 허가서에서 <br /> 연장이 필요한 세부 작업을 체크하세요.
          </p>
        </section>

        <div className="mb-2 flex flex-col gap-2">
          {workTypes.map((work) => (
            <WorkTypeCard
              key={work.id}
              label={work.label}
              selected={selectedIds.has(work.id)}
              onToggle={() => handleToggle(work.id)}
            />
          ))}
        </div>

        <div className="mb-12">
          <StatusInfoCard
            currentEndTime={currentEndTime}
            extendHours={extendHours}
            onExtendHoursChange={setExtendHours}
          />
        </div>
      </main>

      {/* [버튼] */}
      <BottomBar variant="blur">
        <PrimaryButton size="lg" flex="w-full" onClick={handleSubmit}>
          연장신청하기
        </PrimaryButton>
      </BottomBar>
    </div>
  );
}
